import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ArrowLeft,
  Crown,
  UserCheck,
  Plus,
  Mail,
  Building2,
  User,
  ChevronDown,
  LogOut,
  Trash2
} from 'lucide-react';

// Using relative URLs

const SuperAdminAuditors = () => {
  const navigate = useNavigate();
  const { user, logout, emulateCustomer } = useAuth();
  const [auditors, setAuditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: ''
  });

  useEffect(() => {
    fetchAuditors();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchAuditors = async () => {
    try {
      const response = await axios.get(`/api/admin/auditors`, getAuthHeaders());
      setAuditors(response.data.auditors || []);
    } catch (error) {
      console.error('Failed to load auditors:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication failed. Please login again.');
        // Redirect to login after a short delay
        setTimeout(() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load auditors');
      }
      
      setAuditors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAuditor = async () => {
    if (!formData.email || !formData.full_name || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(
        `/api/admin/create-auditor`,
        formData,
        getAuthHeaders()
      );
      toast.success('Auditor created successfully!');
      setShowCreateDialog(false);
      setFormData({ email: '', full_name: '', password: '' });
      fetchAuditors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create auditor');
      console.error(error);
    }
  };

  const handleDeleteAuditor = async (auditorId) => {
    if (!window.confirm('Are you sure you want to delete this auditor?')) {
      return;
    }

    try {
      await axios.delete(
        `/api/admin/auditors/${auditorId}`,
        getAuthHeaders()
      );
      toast.success('Auditor deleted successfully');
      fetchAuditors();
    } catch (error) {
      toast.error('Failed to delete auditor');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div>
                <h1 className="text-2xl font-bold text-slate-900">All Auditors</h1>
                <p className="text-sm text-slate-600">Create and manage auditors for customer assignments</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats and Create Button */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-8">
            <div>
              <p className="text-sm text-slate-600">Total Auditors</p>
              <p className="text-3xl font-bold text-slate-900">{auditors.length}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Active Assignments</p>
              <p className="text-3xl font-bold text-navy-600">
                {auditors.reduce((sum, a) => sum + (a.assigned_organizations?.length || 0), 0)}
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-navy-600 hover:bg-navy-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Auditor
          </Button>
        </div>

        {/* Auditors List */}
        {auditors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UserCheck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Auditors Created</h3>
              <p className="text-slate-600 mb-4">
                Create platform-level auditors who can be assigned to customer audits.
              </p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-navy-600 hover:bg-navy-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Auditor
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auditors.map((auditor) => (
              <Card key={auditor.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{auditor.full_name || auditor.email}</CardTitle>
                      <CardDescription className="mt-1 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {auditor.email}
                      </CardDescription>
                    </div>
                    <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                      <UserCheck className="w-3 h-3 mr-1" />
                      Auditor
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Workspace:</span>
                      <span className="font-semibold text-slate-900">
                        {auditor.workspace_name || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Status:</span>
                      <Badge variant="outline" className="text-navy-700 border-emerald-300">
                        {auditor.status || 'active'}
                      </Badge>
                    </div>
                    
                    <div className="pt-3 border-t border-slate-200 space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => emulateCustomer(auditor.id, auditor.email)}
                        className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Emulate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAuditor(auditor.id)}
                        className="w-full border-red-300 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Auditor
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Auditor Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Platform Auditor</DialogTitle>
            <DialogDescription>
              Create an auditor who can be assigned to any customer's audits. They will not belong to any specific organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                placeholder="John Doe"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="auditor@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">
                Minimum 8 characters. Auditor can change this after first login.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAuditor} className="bg-navy-600 hover:bg-navy-700">
              Create Auditor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminAuditors;
